import { useState, useEffect, useRef } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { Loader2, X } from "lucide-react";
import { toast } from "react-hot-toast";

import SelectMenu from "./crm/SelectMenu";
const reminderOptions = [
  { label: "5 minutes before", value: 5 },
  { label: "10 minutes before", value: 10 },
  { label: "30 minutes before", value: 30 },
  { label: "1 hour before", value: 60 },
  { label: "1 day before", value: 1440 },
];

const searchOptions = [
  { label: "Search by Name", value: "search-name" },
  { label: "Search by Email", value: "search-email" },
  { label: "Search by Mobile", value: "search-mobile" },
  { label: "External Numbers", value: "external" },
];

const AddCaseEventModal = ({ isOpen, onClose, caseData, onEventAdded }) => {
  const { globalUser } = useAuth();
  const [userMobile, setUserMobile] = useState("");

  // Form state
  const [newEvent, setNewEvent] = useState({ title: "", description: "" });
  const [date, setDate] = useState("");
  const [time, setTime] = useState("08:00");
  const [isSaving, setIsSaving] = useState(false);

  // Reminder timing
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState(5);
  const [useCustomOffset, setUseCustomOffset] = useState(false);
  const [customOffset, setCustomOffset] = useState("");
  const [customUnit, setCustomUnit] = useState("minutes");
  const [useSpecificTime, setUseSpecificTime] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");

  // Member selection
  const [sendToSelfOnly, setSendToSelfOnly] = useState(false);
  const [selectedOption, setSelectedOption] = useState({
    type: "search",
    value: "name",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [externalNumbers, setExternalNumbers] = useState("");
  const [validExternalNumbers, setValidExternalNumbers] = useState([]);
  const searchTimeoutRef = useRef(null);

  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);

  const isValidPhoneNumber = (number) => /^\+\d+$/.test(number);

  useEffect(() => {
    if (isOpen && caseData) {
      fetchUserMobile();
      if (caseData.nextHearingDate) {
        const convertedDate = convertToInputFormat(caseData.nextHearingDate);
        setDate(convertedDate);
      }
    }
    if (!isOpen) {
      // Reset
      setNewEvent({ title: "", description: "" });
      setDate("");
      setTime("08:00");
      setSendToSelfOnly(false);
      setSelectedOption({ type: "search", value: "name" });
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setDisplayedUsers([]);
      setSelectedUsers(new Set());
      setExternalNumbers("");
      setUseCustomOffset(false);
      setUseSpecificTime(false);
      setReminderOffsetMinutes(5);
    }
  }, [isOpen, caseData]);

  // Load users and teams
  useEffect(() => {
    if (isOpen && globalUser?.uid) {
      const loadData = async () => {
        setLoadingUsers(true);
        try {
          const [lawyersSnapshot, clientsSnapshot] = await Promise.all([
            getDocs(collection(db, "lawyers")),
            getDocs(collection(db, "client")),
          ]);
          const lawyersData = lawyersSnapshot.docs.map((d) => ({ id: d.id, ...d.data(), userType: "lawyer" }));
          const clientsData = clientsSnapshot.docs.map((d) => ({ id: d.id, ...d.data(), userType: "client" }));
          const usersData = [...lawyersData, ...clientsData].filter((u) => u.id !== globalUser.uid);
          setAllUsers(usersData);

          let currentUserDoc = await getDoc(
            doc(db, "lawyers", globalUser.uid)
          );
          if (!currentUserDoc.exists()) {
            currentUserDoc = await getDoc(doc(db, "client", globalUser.uid));
          }
          if (currentUserDoc.exists()) {
            setCurrentUserData({
              id: globalUser.uid,
              ...currentUserDoc.data(),
            });
          }

          const teamsQuery = query(
            collection(db, "teams"),
            where("ownerId", "==", globalUser.uid)
          );
          const teamsSnapshot = await getDocs(teamsQuery);
          setTeams(
            teamsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
          );
        } catch (err) {
          console.error("Error loading users:", err);
        } finally {
          setLoadingUsers(false);
        }
      };
      loadData();
    }
  }, [isOpen, globalUser?.uid]);

  // Debounce search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  // Filter users by search
  useEffect(() => {
    if (selectedOption.type === "search" && debouncedSearchTerm) {
      const lower = debouncedSearchTerm.toLowerCase().trim();
      const filtered = allUsers.filter((user) => {
        const name = user.name?.toLowerCase() || "";
        const email = user.email?.toLowerCase() || "";
        const mobile = user.mobileNumber?.replace(/\D/g, "") || "";
        const normalizedSearch = lower.replace(/\D/g, "");
        switch (selectedOption.value) {
          case "email":
            return email.includes(lower);
          case "mobile":
            return mobile.includes(normalizedSearch);
          default:
            return name.includes(lower);
        }
      });
      setDisplayedUsers(filtered);
    } else if (selectedOption.type === "search") {
      setDisplayedUsers([]);
    }
  }, [selectedOption, debouncedSearchTerm, allUsers]);

  // Fetch team members
  useEffect(() => {
    if (selectedOption.type === "team") {
      const fetchTeamMembers = async () => {
        setLoadingUsers(true);
        try {
          const teamDocSnap = await getDoc(
            doc(db, "teams", selectedOption.value)
          );
          if (teamDocSnap.exists()) {
            const memberIds = teamDocSnap.data().members || [];
            if (memberIds.length > 0) {
              const memberDocs = await Promise.all(
                memberIds.map((id) => getDoc(doc(db, "lawyers", id)))
              );
              setDisplayedUsers(
                memberDocs
                  .filter((d) => d.exists())
                  .map((d) => ({ id: d.id, ...d.data() }))
              );
            } else {
              setDisplayedUsers([]);
            }
          }
        } catch (err) {
          console.error("Error fetching team members:", err);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchTeamMembers();
    }
  }, [selectedOption]);

  // Validate external numbers
  useEffect(() => {
    if (selectedOption.type === "external") {
      const numbers = externalNumbers
        .split(",")
        .map((n) => n.trim())
        .filter(isValidPhoneNumber);
      setValidExternalNumbers(numbers);
    } else {
      setValidExternalNumbers([]);
    }
  }, [externalNumbers, selectedOption]);

  // Calendar icon styling
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      input[type="date"]::-webkit-calendar-picker-indicator,
      input[type="time"]::-webkit-calendar-picker-indicator {
        filter: invert(1) brightness(1.5);
        cursor: pointer;
        opacity: 1;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchUserMobile = async () => {
    try {
      const userQuery = query(
        collection(db, "lawyers"),
        where("uid", "==", globalUser.uid)
      );
      const userSnapshot = await getDocs(userQuery);
      if (!userSnapshot.empty) {
        setUserMobile(
          userSnapshot.docs[0].data().mobileWithCountryCode || ""
        );
      }
    } catch (error) {
      console.error("Error fetching user mobile:", error);
    }
  };

  const convertToInputFormat = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return dateStr;
  };

  const formatTimeString = (timeStr) => {
    if (!timeStr) return "";
    try {
      const [hours, minutes] = timeStr.split(":");
      const d = new Date();
      d.setHours(parseInt(hours, 10));
      d.setMinutes(parseInt(minutes, 10));
      return d.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return timeStr;
    }
  };

  const generateTimestamp = (dateStr, timeStr) => {
    const dateTimeString = `${dateStr}T${timeStr || "00:00"}:00`;
    const newDate = new Date(dateTimeString);
    return isNaN(newDate.getTime())
      ? new Date(dateStr).getTime()
      : newDate.getTime();
  };

  const calculateOffsetMinutes = () => {
    if (useCustomOffset) {
      const offset = parseInt(customOffset, 10);
      if (isNaN(offset) || offset <= 0) return 0;
      switch (customUnit) {
        case "hours":
          return offset * 60;
        case "days":
          return offset * 60 * 24;
        default:
          return offset;
      }
    }
    return reminderOffsetMinutes;
  };

  const handleUserSelectionChange = (userId, isChecked) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      isChecked ? newSet.add(userId) : newSet.delete(userId);
      return newSet;
    });
  };

  const handleSelectAllChange = (isChecked) => {
    setSelectedUsers((prev) => {
      const newSet = new Set(prev);
      displayedUsers.forEach((u) =>
        isChecked ? newSet.add(u.id) : newSet.delete(u.id)
      );
      return newSet;
    });
  };

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val.startsWith("search-")) {
      setSelectedOption({ type: "search", value: val.split("-")[1] });
    } else if (val.startsWith("team-")) {
      setSelectedOption({ type: "team", value: val.split("-")[1] });
      setSearchTerm("");
    } else if (val === "external") {
      setSelectedOption({ type: "external" });
      setSearchTerm("");
      setDisplayedUsers([]);
      setSelectedUsers(new Set());
    } else {
      setSelectedOption({ type: "search", value: "name" });
    }
  };

  const teamOptions = teams
    .filter((t) => !t.isDefault)
    .map((t) => ({
      label: `Team: ${t.teamName}`,
      value: `team-${t.id}`,
    }));
  const allOptions = [...searchOptions, ...teamOptions];

  const isAllDisplayedSelected =
    displayedUsers.length > 0 &&
    displayedUsers.every((u) => selectedUsers.has(u.id));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newEvent.title || !date || !time) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate recipients
    if (!sendToSelfOnly) {
      if (
        selectedOption.type !== "external" &&
        selectedUsers.size === 0
      ) {
        toast.error("Please select at least one recipient or check 'Send to myself only'");
        return;
      }
      if (
        selectedOption.type === "external" &&
        validExternalNumbers.length === 0
      ) {
        toast.error("Please enter at least one valid phone number");
        return;
      }
    }

    setIsSaving(true);
    try {
      const timestamp = generateTimestamp(date, time);
      const now = Date.now();

      const eventData = {
        title: newEvent.title,
        date: date,
        time: formatTimeString(time),
        time1: timestamp,
        mobile: userMobile || null,
        "Case No": caseData.caseNumber,
        description: newEvent.description || "",
        isScheduled: true,
        isGoogleEvent: false,
      };

      const docRef = await addDoc(
        collection(db, "lawyers", globalUser.uid, "events"),
        eventData
      );

      // Calculate reminder time
      let reminderDateTime;
      if (useSpecificTime) {
        if (!reminderDate || !reminderTime) {
          toast.error("Please select both date and time for the reminder.");
          setIsSaving(false);
          return;
        }
        reminderDateTime = new Date(`${reminderDate}T${reminderTime}:00`);
        if (isNaN(reminderDateTime.getTime())) {
          toast.error("Invalid reminder date or time format.");
          setIsSaving(false);
          return;
        }
      } else {
        const offsetMinutes = calculateOffsetMinutes();
        if (offsetMinutes <= 0) {
          toast.error("Invalid custom offset.");
          setIsSaving(false);
          return;
        }
        reminderDateTime = new Date(timestamp - offsetMinutes * 60 * 1000);
      }

      if (reminderDateTime.getTime() <= now) {
        toast.error("Reminder time is in the past.");
        setIsSaving(false);
        return;
      }

      const reminderTimestamp = Timestamp.fromDate(reminderDateTime);

      // Build recipients list
      let internalRecipients = [];
      let externalRecipientNumbers = [];

      if (sendToSelfOnly) {
        // Only the current user at the chosen offset time
        internalRecipients = [{ id: globalUser.uid }];
      } else if (selectedOption.type === "external") {
        // External phone numbers + self
        externalRecipientNumbers = validExternalNumbers;
        internalRecipients = [{ id: globalUser.uid }];
      } else {
        // Selected users + always include self
        const selectedList = allUsers.filter((u) => selectedUsers.has(u.id));
        const selfAlreadyIncluded = selectedList.some(
          (u) => u.id === globalUser.uid
        );
        if (!selfAlreadyIncluded) {
          internalRecipients = [
            { id: globalUser.uid },
            ...selectedList,
          ];
        } else {
          internalRecipients = selectedList;
        }
      }

      const batch = writeBatch(db);
      const eventRemindersRef = collection(db, "eventReminders");
      const externalRemindersRef = collection(db, "externalEventReminders");

      const caseId = caseData.documentId || caseData.id || "";

      // Create reminder docs for internal recipients at the chosen offset time
      for (const recipient of internalRecipients) {
        const reminderDoc = {
          eventId: docRef.id,
          reminderTime: reminderTimestamp,
          eventTitle: newEvent.title,
          caseNo: caseData.caseNumber || "",
          caseId: caseId,
          collection: "events",
          status: "pending",
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
          recipientId: recipient.id,
        };
        batch.set(doc(eventRemindersRef), reminderDoc);
      }

      // Create reminder docs for external phone numbers
      for (const phoneNumber of externalRecipientNumbers) {
        const reminderDoc = {
          eventId: docRef.id,
          reminderTime: reminderTimestamp,
          eventTitle: newEvent.title,
          caseNo: caseData.caseNumber || "",
          phoneNumber: phoneNumber,
          status: "pending",
          createdAt: new Date(),
          scheduledBy: globalUser.uid,
        };
        batch.set(doc(externalRemindersRef), reminderDoc);
      }

      await batch.commit();

      const totalRecipients = internalRecipients.length + externalRecipientNumbers.length;
      toast.success(`Event added! Reminders scheduled for ${totalRecipients} recipient(s).`);
      setNewEvent({ title: "", description: "" });
      onEventAdded();
      onClose();
    } catch (error) {
      console.error("Error adding event:", error);
      toast.error("Failed to add event");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[1100] p-4 backdrop-blur-sm">
      <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white">Add New Event</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Form */}
        <form
          onSubmit={handleSubmit}
          className="p-5 overflow-y-auto flex-grow"
        >
          <div className="space-y-3">
            {/* Title */}
            <input
              type="text"
              placeholder="Enter Event Title"
              value={newEvent.title}
              onChange={(e) => {
                const text = e.target.value.slice(0, 20);
                const cap = text.charAt(0).toUpperCase() + text.slice(1);
                setNewEvent({ ...newEvent, title: cap });
              }}
              maxLength={20}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#31507F] text-sm"
              required
            />

            {/* Date */}
            <input
              type="date"
              ref={dateInputRef}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#31507F] text-sm"
              required
            />

            {/* Event Time */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Event Time
              </label>
              <input
                type="time"
                ref={timeInputRef}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#31507F] text-sm"
                required
              />
            </div>

            {/* Description */}
            <div>
              <textarea
                placeholder="Enter Event Description (optional)"
                value={newEvent.description}
                onChange={(e) =>
                  setNewEvent({
                    ...newEvent,
                    description: e.target.value.slice(0, 1000),
                  })
                }
                maxLength={1000}
                rows={2}
                className="w-full bg-[#06030f] text-white p-3 rounded-md focus:outline-none focus:ring-1 focus:ring-[#31507F] text-sm resize-none"
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {newEvent.description.length}/1000
              </div>
            </div>

            {/* Send to myself only */}
            <label className="flex items-center text-sm text-white">
              <input
                type="checkbox"
                checked={sendToSelfOnly}
                onChange={(e) => setSendToSelfOnly(e.target.checked)}
                className="mr-2"
              />
              Send to myself only
            </label>

            {/* Member selection */}
            {!sendToSelfOnly && (
              <>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    placeholder={
                      selectedOption.type === "search"
                        ? `Enter ${selectedOption.value.charAt(0).toUpperCase() + selectedOption.value.slice(1)}`
                        : "Select Members"
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={selectedOption.type === "team"}
                    className="flex-1 min-w-0 w-full p-2 pl-3 rounded bg-[#06030f] border border-gray-600 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                  <SelectMenu
                    value={
                      selectedOption.type === "external"
                        ? "external"
                        : `${selectedOption.type}-${selectedOption.value}`
                    }
                    onChange={handleSelectChange}
                    className="flex-1 min-w-0 w-full p-2 rounded bg-[#06030f] border border-gray-600 text-white focus:outline-none focus:border-blue-500 text-sm truncate"
                  >
                    <option value="">Select...</option>
                    {allOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </SelectMenu>
                </div>

                <div className="border border-gray-700 rounded bg-[#06030f]/50 min-h-[150px] max-h-[200px] overflow-y-auto flex flex-col">
                  {selectedOption.type === "external" ? (
                    <textarea
                      value={externalNumbers}
                      onChange={(e) => setExternalNumbers(e.target.value)}
                      placeholder="Enter phone numbers separated by commas, e.g., +1234567890, +1987654321"
                      className="w-full p-2 rounded bg-transparent border-none text-white focus:outline-none h-full resize-none text-sm"
                    />
                  ) : loadingUsers ? (
                    <div className="flex-grow flex items-center justify-center text-gray-400">
                      <Loader2 className="animate-spin mr-2" size={16} />{" "}
                      Loading...
                    </div>
                  ) : displayedUsers.length === 0 ? (
                    <p className="text-center text-gray-500 pt-10 px-4 text-sm">
                      {selectedOption.type === "team"
                        ? "This team has no members."
                        : debouncedSearchTerm
                          ? "No users found."
                          : `Start typing to search by ${selectedOption.value}`}
                    </p>
                  ) : (
                    <>
                      <div className="sticky top-0 bg-[#1A1535] z-10 flex items-center px-3 py-2 border-b border-gray-700">
                        <input
                          type="checkbox"
                          checked={isAllDisplayedSelected}
                          onChange={(e) =>
                            handleSelectAllChange(e.target.checked)
                          }
                          className="mr-3 h-4 w-4"
                        />
                        <span className="text-sm text-white">
                          Select All ({displayedUsers.length} displayed /{" "}
                          {selectedUsers.size} selected)
                        </span>
                      </div>
                      {displayedUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center p-3 border-b border-gray-700/50 hover:bg-gray-700/40 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={(e) =>
                              handleUserSelectionChange(
                                user.id,
                                e.target.checked
                              )
                            }
                            className="mr-3 h-4 w-4 flex-shrink-0"
                          />
                          <div className="flex-1 text-sm overflow-hidden">
                            <p className="font-semibold text-gray-100">
                              {user.name || "N/A"}
                            </p>
                            {user.email && (
                              <p className="text-gray-400 truncate text-xs">
                                {user.email}
                              </p>
                            )}
                            {user.mobileNumber && (
                              <p className="text-gray-400 text-xs">
                                {user.mobileNumber}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </>
            )}

            {/* Specific time toggle */}
            <label className="flex items-center text-sm text-white">
              <input
                type="checkbox"
                checked={useSpecificTime}
                onChange={(e) => setUseSpecificTime(e.target.checked)}
                className="mr-2"
              />
              Set specific reminder time
            </label>

            {/* Reminder timing */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Send Reminder:
              </label>
              {useSpecificTime ? (
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={reminderDate}
                    onChange={(e) => setReminderDate(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 rounded bg-[#06030f] border border-gray-600 text-white focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 rounded bg-[#06030f] border border-gray-600 text-white focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <SelectMenu
                    value={useCustomOffset ? "custom" : reminderOffsetMinutes}
                    onChange={(e) => {
                      if (e.target.value === "custom") {
                        setUseCustomOffset(true);
                      } else {
                        setUseCustomOffset(false);
                        setReminderOffsetMinutes(parseInt(e.target.value, 10));
                      }
                    }}
                    className="p-2 rounded bg-[#06030f] border border-gray-600 text-white text-sm"
                  >
                    {reminderOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    <option value="custom">Custom</option>
                  </SelectMenu>
                  {useCustomOffset && (
                    <>
                      <input
                        type="number"
                        value={customOffset}
                        onChange={(e) => setCustomOffset(e.target.value)}
                        placeholder="Enter number"
                        min="1"
                        className="p-2 rounded bg-[#06030f] border border-gray-600 text-white text-sm w-24"
                      />
                      <SelectMenu
                        value={customUnit}
                        onChange={(e) => setCustomUnit(e.target.value)}
                        className="p-2 rounded bg-[#06030f] border border-gray-600 text-white text-sm"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </SelectMenu>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Note */}
            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3">
              <p className="text-blue-300 text-xs">
                <strong>Note:</strong> For sending reminders to people
                outside your organization, select "External Numbers" from
                the dropdown above.
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 rounded-md transition-colors text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 rounded-md transition-colors text-sm flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Saving...
                </>
              ) : sendToSelfOnly ? (
                "Schedule"
              ) : selectedOption.type === "external" ? (
                `Schedule (${validExternalNumbers.length})`
              ) : (
                `Schedule (${selectedUsers.size + 1})`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCaseEventModal;
